/* eslint-disable camelcase */
/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { industries, hasPerm, isOwner } from '../../../shared/utils';
import { getActionFeedbackFormControlInternalId } from '../utils';
import { _timezone } from '../../../shared/constants';
import win from '../../../shared/win';

const team = win.ACTIVE_TEAM || {};

export const timezoneOptions = _timezone.map((timezone) => {
  const zone = {
    id: timezone.id,
    key: timezone.id,
    text: timezone.name,
    value: timezone.zone,
  };
  return zone;
});

const getUserTimezone = () => {
  let clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  clientTimezone = timezoneOptions.find((timezone) => timezone.key === clientTimezone);
  if (clientTimezone) return clientTimezone.value;
  return timezoneOptions[0].value;
};

const accountPermissions = {
  editAccountPermitted: isOwner(team) || hasPerm(win.PERMS.EDIT_ACCOUNT_SETTINGS, team),
  manageSubPermitted: isOwner(team) || hasPerm(win.PERMS.MANAGE_SUBSCRIPTION, team),
  viewCredPermitted: isOwner(team) || hasPerm(win.PERMS.VIEW_CREDENTIALS, team),
  viewSubPermitted: isOwner(team) || hasPerm(win.PERMS.VIEW_SUBSCRIPTION, team),
  addCredPermitted: isOwner(team) || hasPerm(win.PERMS.ADD_CREDENTIALS, team),
  viewQuotaPermitted: isOwner(team) || hasPerm(win.PERMS.VIEW_QUOTA, team)
};

export const industryOptions = industries.map((industry, index) => ({
  value: industry,
  text: industry,
  key: index
}));

export const defaultState = {
  isMandatoryFeedbackFormFieldsSet: {
    cancel_subscription: false,
    delete_account: false,
  },
  activeSidebarTab: 'account-info',
  industryOptions,
  showMobileSidebar: false,
  accountPermissions,
  actionFeedbackForms: {
    cancel_subscription: null,
    delete_account: null,
  },
  userCustomTexts: {},
  subscriptionInfo: {
    email: '',
    subscription_plan: '',
    subscription_start: '',
    subscription_renew: '',
    subscription_url: null,
    trial: false,
    trial_expiry: null,
    trial_expired: false,
    subscription_history: [],
  },
  quotaInfo: {
    forms_used: 0,
    forms_left: 'inf',
    submissions_used: 0,
    submissions_left: 0,
    members_max: 0,
    members_count: 0,
    storage_limit: 0,
    storage_used: 0,
    storage_left: 0,
    forms_views_used: 0,
    max_form_view: 'inf',
    emails_used: 0,
    emails_left: 'inf',
    products_left: 0,
    products_used: 0,
    geolocation_quota: {
      limit: 0,
      remaining: 0,
      used: 0,
    },
    address_autocompletion: {
      limit: 0,
      remaining: 0,
      used: 0,
    },
  },
  userOptions: {
    name: '',
    timezone: '',
    notification_emails: [],
    link_type: 'sheet',
    storage_service: '',
    response_store: '',
  },
  userInfo: {
    first_name: '',
    last_name: '',
    other_names: '',
    industry: '',
    phone_number: '',
    company_name: '',
  },
  credentials: {
    drive_email: null,
    sheets_status: null,
    stripe_credentials: null,
    paypal_credentials: {
      client_id: '',
      test_client_id: '',
    },
    flutterwave_credentials: {
      public_key: '',
      private_key: '',
    },
    paystack_credentials: {
      public_key: '',
      test_public_key: '',
    },
  },
  subscriptionStatus: 'active',
  activeAuthorization: null,
  sheetsStatus: 'success',
  isSaving: false,
  authorizationStatuses: {
    google_drive: false,
    google_sheet: false,
    one_drive: false,
    dropbox: false,
  },
  formTransferLogs: [],
  twoFactorAuthentication: {
    confirmation_code: '',
    isLoading: false,
    qr_code_url: '',
    secret: '',
    status: '',
  },
};

const accountManagerSlice = createSlice({
  name: 'accountManager',
  initialState: defaultState,
  reducers: {
    setActionFeedbackForm: (state, { payload }) => {
      const { form, formType } = payload;
      state.actionFeedbackForms[formType] = form;
    },
    setVitalFieldsOnActionFeedbackForm: (state, { payload }) => {
      const { actionFeedbackForms, userInfo: { first_name, last_name }, subscriptionInfo: { email } } = state;
      const vitalResponseControls = { first_name, last_name, email };
      const { formType, isRetry } = payload;

      // If all mandatory field are available
      if (Object.values(vitalResponseControls).every((value) => value)) {
        const form = actionFeedbackForms[formType];
        
        if (form) {
          if (isRetry) toast.success('Required user feedback data updated successfully!');
          state.isMandatoryFeedbackFormFieldsSet[formType] = true;

          form.definition.pages.forEach((page) => {
            Object.keys(vitalResponseControls).forEach((controlKey) => {
              const internalId = getActionFeedbackFormControlInternalId({
                controlKey,
                formType,
              });

              page.controls.forEach((control) => {
                if (control.internal_id === internalId) {
                  control.value = vitalResponseControls[controlKey];
                }
              });
            });
          });
        }
      } else if (isRetry) {
        toast.warning(`Before deleting your account, please ensure your profile details (first name, last name, and email) are fully updated. This step is essential to avoid any interruptions in the deletion process.`);
      }
    },
    setActionFeedbackControlValue: (state, { payload }) => {
      const { form_type, internal_id, value } = payload;
      const form = state.actionFeedbackForms[form_type];

      if (form) {
        form.definition.pages.forEach((page) => {
          page.controls.forEach((control) => {
            if (control.internal_id === internal_id) control.value = value;
          });
        });
      }
    },
    // Permission authorization
    setAuthorizationStatus: (state, { payload }) => {
      const { key, value } = payload;
      state.authorizationStatuses[key] = value;
    },
    setActiveAuthorization: (state, { payload }) => {
      state.activeAuthorization = payload;
    },
    setActiveUnauthorization: (state, { payload }) => {
      state.activeUnauthorization = payload;
    },
    // Sidebar switching
    setActiveSidebarTab: (state, { payload }) => {
      state.activeSidebarTab = payload;
    },
    setShowMobileSideBar: (state, { payload }) => {
      state.showMobileSidebar = payload;
    },
    // Custom texts
    setUserCustomTexts: (state, { payload }) => {
      state.userCustomTexts = payload;
    },
    setUserCustomText: (state, { payload }) => {
      const { key, value } = payload;
      state.userCustomTexts[key] = value;
    },
    // Api call state
    setIsSaving: (state, { payload }) => {
      state.isSaving = payload;
    },
    // User options
    setUserOptions: (state, { payload }) => {
      state.userOptions = payload;

      if (payload.response_store === 'google_sheet' && payload.results_in_datastore) {
        state.userOptions.response_store = 'formplus_sheet';
      }

      // Null timezone value is a blocker to user options update
      // at least a default value must be set for new users
      state.userOptions.timezone = payload.timezone || getUserTimezone();
    },
    setUserOption: (state, { payload }) => {
      const { key, value } = payload;
      state.userOptions[key] = value;
    },
    // User options
    setUserInfo: (state, { payload }) => {
      if (payload.two_factor_enabled) state.twoFactorAuthentication.status = 'activated';
      state.userInfo = payload;
    },
    editUserInfo: (state, { payload }) => {
      const { key, value } = payload;
      state.userInfo[key] = value;
    },
    // Quota info
    setQuotaInfo: (state, { payload }) => {
      state.quotaInfo = payload;
    },
    // Credential
    setCredentials: (state, { payload }) => {
      state.credentials = payload;
    },
    setCredential: (state, { payload }) => {
      const { name, key, value } = payload;
      state.credentials[name][key] = value;
    },
    // Sheet status
    setSheetsStatus: (state, { payload }) => {
      state.sheetsStatus = payload;
    },
    // Stripe
    revokeStripeAccess: (state, payload) => {
      state.credentials.stripe_credentials = 'revoke';
    },
    // Subscription
    setSubscriptionInfo: (state, { payload }) => {
      state.subscriptionInfo = payload;
    },
    reactivateSubscription: (state, { payload }) => {
      state.subscriptionStatus = payload;
    },
    setSubscriptionStatus: (state, { payload }) => {
      state.subscriptionStatus = payload;
    },
    // Frorm transfer logs
    setFormTransferLogs: (state, { payload }) => {
      state.formTransferLogs = payload;
    },
    // Two Fator Authentication
    setTwoFactorAuthentication: (state, { payload }) => {
      state.twoFactorAuthentication = {
        ...state.twoFactorAuthentication,
        ...payload,
      };
    },
    setIndustryOptions: (state, { payload }) => {
      state.industryOptions = payload;
    }
  },
});

export const {
  setVitalFieldsOnActionFeedbackForm,
  setActionFeedbackControlValue,
  setTwoFactorAuthentication,
  setAuthorizationStatus,
  setActiveAuthorization,
  setActiveUnauthorization,
  reactivateSubscription,
  setSubscriptionStatus,
  setActionFeedbackForm,
  setShowMobileSideBar,
  setActiveSidebarTab,
  setSubscriptionInfo,
  setFormTransferLogs,
  setUserCustomTexts,
  revokeStripeAccess,
  setIndustryOptions,
  setUserCustomText,
  setSheetsStatus,
  setCredentials,
  setUserOptions,
  setUserOption,
  setCredential,
  editUserInfo,
  setQuotaInfo,
  setUserInfo,
  setIsSaving,
} = accountManagerSlice.actions;

export default accountManagerSlice.reducer;
